// src/ui/screens/post/ListingImagesPage.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

// ── ListingImagesPage ─────────────────────────────────────────────────────────

export const ListingImagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const listingId = location.state?.listingId || '';
  const listingTitle = location.state?.listingTitle || '';
  const imageUrls = location.state?.imageUrls || [];
  const initialIndex = location.state?.initialIndex || 0;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showControls, setShowControls] = useState(true);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [imageLoading, setImageLoading] = useState({});

  const pageContainerRef = useRef(null);
  const thumbScrollRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  // ── Keyboard navigation ───────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < imageUrls.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else if (e.key === 'Escape') {
        navigate(-1);
      } else if (e.key === ' ' || e.key === 'Space') {
        e.preventDefault();
        toggleControls();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, imageUrls.length, navigate]);

  // ── Scroll thumbnail to active index ──────────────────────────────────────

  useEffect(() => {
    if (thumbScrollRef.current) {
      const thumbWidth = 72; // 64 + 8 margin
      const containerWidth = thumbScrollRef.current.clientWidth;
      const offset = currentIndex * thumbWidth - containerWidth / 2 + thumbWidth / 2;
      const maxScroll = thumbScrollRef.current.scrollWidth - containerWidth;
      thumbScrollRef.current.scrollTo({
        left: Math.max(0, Math.min(offset, maxScroll)),
        behavior: 'smooth',
      });
    }
  }, [currentIndex]);

  // ── Toggle controls ──────────────────────────────────────────────────────

  const toggleControls = () => {
    setShowControls(!showControls);
  };

  // ── Navigation ────────────────────────────────────────────────────────────

  const goTo = (index) => {
    if (index < 0 || index >= imageUrls.length) return;
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  const goPrevious = () => goTo(currentIndex - 1);
  const goNext = () => goTo(currentIndex + 1);

  // ── Touch handlers ──────────────────────────────────────────────────────

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = touchStartY.current - touchEndY.current;
    const threshold = 50;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
      if (diffX > 0 && currentIndex < imageUrls.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setIsZoomed(false);
      } else if (diffX < 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
        setIsZoomed(false);
      }
    } else if (Math.abs(diffY) > threshold) {
      toggleControls();
    }
  };

  // ── Share ─────────────────────────────────────────────────────────────────

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const url = `https://filatom.com/listing/${listingId}`;
      if (navigator.share) {
        await navigator.share({
          title: listingTitle,
          text: listingTitle,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(`${listingTitle}\n${url}`);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
    } finally {
      setIsSharing(false);
    }
  };

  // ── Image loading ────────────────────────────────────────────────────────

  const handleImageLoad = (index) => {
    setImageLoading(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index) => {
    setImageLoading(prev => ({ ...prev, [index]: false }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!imageUrls || imageUrls.length === 0) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg className="w-16 h-16 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-white/45 text-sm">No images available</span>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-full bg-filatom-card text-white text-sm"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const total = imageUrls.length;
  const currentUrl = imageUrls[currentIndex];
  const isLoading = imageLoading[currentIndex] !== false;

  return (
    <div
      className="fixed inset-0 bg-black"
      onClick={toggleControls}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      ref={pageContainerRef}
    >
      {/* Main image */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
            transition: 'transform 0.3s ease-out',
          }}
        >
          <img
            src={currentUrl}
            alt={`Photo ${currentIndex + 1}`}
            className={`w-full h-full object-contain transition-opacity duration-300 ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={() => handleImageLoad(currentIndex)}
            onError={() => handleImageError(currentIndex)}
            onDoubleClick={() => setIsZoomed(!isZoomed)}
          />
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-3 border-filatom-primary border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Top controls */}
      <div
        className={`absolute top-0 left-0 right-0 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pt-safe-top px-3 pb-3"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate drop-shadow-lg">
                {listingTitle}
              </p>
              <p className="text-white/60 text-xs">
                Photo {currentIndex + 1} of {total}
              </p>
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); setShowThumbnails(!showThumbnails); }}
              className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showThumbnails ? "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>

            {isSharing ? (
              <div className="w-9.5 h-9.5 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      {showControls && !isZoomed && total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrevious(); }}
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-opacity ${
              currentIndex > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className={`absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-opacity ${
              currentIndex < total - 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Zoom reset hint */}
      {isZoomed && (
        <div className="absolute bottom-32 left-0 right-0 flex justify-center">
          <div
            className="px-4.5 py-2 rounded-full flex items-center gap-2"
            style={{
              backgroundColor: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10a1 1 0 11-2 0 1 1 0 012 0z" />
            </svg>
            <span className="text-white text-xs">Tap to reset zoom</span>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div
        className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pt-4 pb-safe-bottom"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
          }}
        >
          {/* Dot indicator */}
          {total <= 8 && (
            <div className="flex justify-center gap-1.5 mb-2.5">
              {imageUrls.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === currentIndex
                      ? 'w-5.5 bg-filatom-primary'
                      : 'w-1.5 bg-white/35'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Thumbnail strip */}
          {showThumbnails && total > 1 && (
            <div
              ref={thumbScrollRef}
              className="overflow-x-auto hide-scrollbar px-4"
              style={{ scrollbarWidth: 'none' }}
            >
              <div className="flex gap-2">
                {imageUrls.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-200 ${
                      i === currentIndex
                        ? 'ring-2 ring-filatom-primary ring-offset-1 ring-offset-black'
                        : 'opacity-60 hover:opacity-80'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`Thumb ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className={`absolute inset-0 ${i !== currentIndex ? 'bg-black/35' : ''}`} />
                    <span
                      className={`absolute bottom-1 right-1.5 text-[9px] font-bold ${
                        i === currentIndex ? 'text-filatom-primary' : 'text-white/70'
                      }`}
                    >
                      {i + 1}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hide scrollbar styles */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ListingImagesPage;